import {
	getInterventionTaskOptions,
	getFullVisitOptions,
	getWorkflowStageTaskOptions,
	type FullVisitViewModel,
	getVisitOptions,
	getOasisTaskOptions,
	getInterventionTaskVisitBucketCategoryOptions,
	getOasisTaskVisitBucketCategoryOptions,
	getFullVisitBucketOptions,
	getVisitStatusOptions
} from '@generated';
import { QueryClient, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { produce } from 'immer';
import { useGetCategoryLabel } from '~/libraries/categories/useGetCategoryLabel';
import type { ProblemDetails } from '~/util/http/types';

export function useGetFullVisitOptions(visitId: string) {
	const queryClient = useQueryClient();
	const getCategoryLabel = useGetCategoryLabel();
	return getFullVisitOptions(visitId, (result: FullVisitViewModel) => {
		for (const visitBucket of result.visitBuckets) {
			if (!visitBucket.orderedItems || visitBucket.orderedItems.length === 0) {
				continue;
			}
			queryClient.setQueryData(getFullVisitBucketOptions(visitBucket.id).queryKey, visitBucket);
			for (const item of visitBucket.orderedItems) {
				if (item.category) {
					item.category.label = getCategoryLabel(item.category.name);

					if (item.category.interventionTasks.length > 0) {
						queryClient.setQueryData(
							getInterventionTaskVisitBucketCategoryOptions(visitBucket.id, item.category.name).queryKey,
							item.category.interventionTasks
						);
					} else if (item.category.oasisTasks.length > 0) {
						queryClient.setQueryData(
							getOasisTaskVisitBucketCategoryOptions(visitBucket.id, item.category.name).queryKey,
							item.category.oasisTasks
						);
					}

					for (const interventionTask of item.category.interventionTasks) {
						queryClient.setQueryData(getInterventionTaskOptions(interventionTask.id).queryKey, interventionTask);
					}
					for (const oasksTask of item.category.oasisTasks) {
						queryClient.setQueryData(getOasisTaskOptions(oasksTask.id).queryKey, oasksTask);
					}
				} else if (item.interventionTask) {
					queryClient.setQueryData(getInterventionTaskOptions(item.interventionTask.id).queryKey, item.interventionTask);
				} else if (item.workflowStageTask) {
					queryClient.setQueryData(getWorkflowStageTaskOptions(item.workflowStageTask.id).queryKey, item.workflowStageTask);
				} else if (item.oasisTask) {
					queryClient.setQueryData(getOasisTaskOptions(item.oasisTask.id).queryKey, item.oasisTask);
				}
			}
		}
	});
}

function createGetCategoryOptions(
	visitBucketId: string,
	categoryName: string,
	interventionBucket: boolean,
	queryClient: QueryClient,
	fullVisitOptionsQueryKey: ReturnType<typeof getFullVisitOptions>['queryKey']
) {
	if (interventionBucket) {
		return getInterventionTaskVisitBucketCategoryOptions(visitBucketId, categoryName, result => {
			const fullVisit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
			if (fullVisit) {
				queryClient.setQueryData(
					fullVisitOptionsQueryKey,
					produce(fullVisit, draft => {
						const bucket = draft.visitBuckets.find(b => b.id === visitBucketId);
						if (!bucket || !bucket.orderedItems || bucket.orderedItems.length === 0) {
							return;
						}
						const category = bucket.orderedItems.find(i => i.category && i.category.name === categoryName)?.category;
						if (!category) {
							return;
						}
						category.interventionTasks = category.interventionTasks.map(task => {
							const updatedTask = result.find(t => t.id === task.id);
							return updatedTask ? { ...task, ...updatedTask } : task;
						});
					})
				);
			}
		});
	}

	return getOasisTaskVisitBucketCategoryOptions(visitBucketId, categoryName, result => {
		const fullVisit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
		if (fullVisit) {
			queryClient.setQueryData(
				fullVisitOptionsQueryKey,
				produce(fullVisit, draft => {
					const bucket = draft.visitBuckets.find(b => b.id === visitBucketId);
					if (!bucket || !bucket.orderedItems || bucket.orderedItems.length === 0) {
						return;
					}
					const category = bucket.orderedItems.find(i => i.category && i.category.name === categoryName)?.category;
					if (!category) {
						return;
					}
					category.oasisTasks = category.oasisTasks.map(task => {
						const updatedTask = result.find(t => t.id === task.id);
						return updatedTask ? { ...task, ...updatedTask } : task;
					});
				})
			);
		}
	});
}

function createGetInterventionTaskOptions(
	visitBucketId: string,
	interventionTaskId: string,
	queryClient: QueryClient,
	fullVisitOptionsQueryKey: ReturnType<typeof getFullVisitOptions>['queryKey']
) {
	return getInterventionTaskOptions(interventionTaskId, result => {
		const fullVisit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
		if (fullVisit) {
			queryClient.setQueryData(
				fullVisitOptionsQueryKey,
				produce(fullVisit, draft => {
					const bucket = draft.visitBuckets.find(b => b.id === visitBucketId);
					if (!bucket || !bucket.orderedItems || bucket.orderedItems.length === 0) {
						return;
					}
					const item = bucket.orderedItems.find(i => i.interventionTask && i.interventionTask.id === interventionTaskId);
					if (!item) {
						return;
					}
					item.interventionTask = result;
				})
			);
		}
	});
}

function createGetOasisTaskOptions(
	visitBucketId: string,
	oasisTaskId: string,
	queryClient: QueryClient,
	fullVisitOptionsQueryKey: ReturnType<typeof getFullVisitOptions>['queryKey']
) {
	return getOasisTaskOptions(oasisTaskId, result => {
		const fullVisit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
		if (fullVisit) {
			queryClient.setQueryData(
				fullVisitOptionsQueryKey,
				produce(fullVisit, draft => {
					const bucket = draft.visitBuckets.find(b => b.id === visitBucketId);
					if (!bucket || !bucket.orderedItems || bucket.orderedItems.length === 0) {
						return;
					}
					const item = bucket.orderedItems.find(i => i.oasisTask && i.oasisTask.id === oasisTaskId);
					if (!item) {
						return;
					}
					item.oasisTask = result;
				})
			);
		}
	});
}

type VisitBucketItemRefreshType = 'intervention' | 'oasis' | 'intervention-category' | 'oasis-category';

export function useGetVisitBucketItemOptions(
	visitId: string,
	visitBucketId: string,
	visitItemId: string,
	type: VisitBucketItemRefreshType
): UseQueryOptions<unknown, AxiosError<ProblemDetails>, unknown, readonly unknown[]> {
	const queryClient = useQueryClient();
	const fullVisitOptionsQueryKey = getFullVisitOptions(visitId).queryKey;

	const baseOptions = (
		type === 'intervention-category' || type === 'oasis-category'
			? createGetCategoryOptions(visitBucketId, visitItemId, type === 'intervention-category', queryClient, fullVisitOptionsQueryKey)
			: type === 'intervention'
			? createGetInterventionTaskOptions(visitBucketId, visitItemId, queryClient, fullVisitOptionsQueryKey)
			: createGetOasisTaskOptions(visitBucketId, visitItemId, queryClient, fullVisitOptionsQueryKey)
	) as UseQueryOptions<unknown, AxiosError<ProblemDetails>, unknown, readonly unknown[]>;

	return {
		...baseOptions,
		enabled: !!(visitId && visitBucketId && visitItemId),
		initialData: () => {
			const visit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
			if (!visit) return undefined;
			if (type === 'intervention-category') {
				return visit.visitBuckets.find(b => b.id === visitBucketId)?.orderedItems.find(i => i.category && i.category.name === visitItemId)
					?.category?.interventionTasks;
			} else if (type === 'intervention') {
				return visit.visitBuckets
					.find(b => b.id === visitBucketId)
					?.orderedItems.find(i => i.interventionTask && i.interventionTask.id === visitItemId)?.interventionTask;
			} else if (type === 'oasis-category') {
				return visit.visitBuckets.find(b => b.id === visitBucketId)?.orderedItems.find(i => i.category && i.category.name === visitItemId)
					?.category?.oasisTasks;
			}
			return visit.visitBuckets.find(b => b.id === visitBucketId)?.orderedItems.find(i => i.oasisTask && i.oasisTask.id === visitItemId)
				?.oasisTask;
		},
		initialDataUpdatedAt: () => queryClient.getQueryState(fullVisitOptionsQueryKey)?.dataUpdatedAt
	};
}

export function useGetFullVisitBucketOptions(visitId: string, visitBucketId: string) {
	const queryClient = useQueryClient();
	const fullVisitOptionsQueryKey = getFullVisitOptions(visitId).queryKey;
	return {
		...getFullVisitBucketOptions(visitBucketId, result => {
			const fullVisit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
			if (fullVisit) {
				queryClient.setQueryData(
					fullVisitOptionsQueryKey,
					produce(fullVisit, draft => {
						const index = draft.visitBuckets.findIndex(b => b.id === visitBucketId);
						if (index === -1) {
							return;
						}
						draft.visitBuckets[index] = result;
					})
				);
			}
		}),
		initialData: () => {
			const fullVisit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
			if (!fullVisit) {
				return undefined;
			}
			return fullVisit.visitBuckets.find(b => b.id === visitBucketId);
		},
		initialDataUpdatedAt: () => queryClient.getQueryState(fullVisitOptionsQueryKey)?.dataUpdatedAt
	};
}

export function useGetWorkflowStageTaskForVisitOptions(visitId: string, visitBucketId: string, workflowStageTaskId: string) {
	const queryClient = useQueryClient();
	const fullVisitOptionsQueryKey = getFullVisitOptions(visitId).queryKey;

	return {
		...getWorkflowStageTaskOptions(workflowStageTaskId, result => {
			const fullVisit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
			if (fullVisit) {
				queryClient.setQueryData(
					fullVisitOptionsQueryKey,
					produce(fullVisit, draft => {
						const bucket = draft.visitBuckets.find(b => b.id === visitBucketId);
						if (!bucket || !bucket.orderedItems || bucket.orderedItems.length === 0) {
							return;
						}
						const item = bucket.orderedItems.find(i => i.workflowStageTask && i.workflowStageTask.id === workflowStageTaskId);
						if (!item) {
							return;
						}
						item.workflowStageTask = result;
					})
				);
			}
		}),
		enabled: !!(visitId && visitBucketId && workflowStageTaskId),
		initialData: () => {
			const visit = queryClient.getQueryData(fullVisitOptionsQueryKey);
			if (!visit) {
				return undefined;
			}
			return visit.visitBuckets
				.find(b => b.id === visitBucketId)
				?.orderedItems.find(i => i.workflowStageTask && i.workflowStageTask.id === workflowStageTaskId)?.workflowStageTask;
		},
		initialDataUpdatedAt: () => queryClient.getQueryState(fullVisitOptionsQueryKey)?.dataUpdatedAt
	};
}

export function useGetVisitStatusOptions(visitId: string) {
	const queryClient = useQueryClient();
	const fullVisitOptionsQueryKey = getFullVisitOptions(visitId).queryKey;

	return {
		...getVisitStatusOptions(visitId, result => {
			const fullVisit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
			if (fullVisit) {
				queryClient.setQueryData(
					fullVisitOptionsQueryKey,
					produce(fullVisit, draft => {
						draft.visitStatusId = result.visitStatusId;
						draft.visitStatusLabel = result.visitStatusLabel;
						draft.promptingToAdmit = result.promptingToAdmit;
						for (const bucketStats of result.visitBuckets) {
							const visitBucket = draft.visitBuckets.find(b => b.id === bucketStats.id);
							if (!visitBucket) {
								continue;
							}
							visitBucket.visitBucketStatusId = bucketStats.visitBucketStatusId;
							visitBucket.visitBucketStatusLabel = bucketStats.visitBucketStatusLabel;
						}
					})
				);
			}
		}),
		initialData: () => {
			const fullVisit = queryClient.getQueryData(fullVisitOptionsQueryKey);
			if (!fullVisit) {
				return undefined;
			}
			return {
				visitStatusLabel: fullVisit.visitStatusLabel,
				visitStatusId: fullVisit.visitStatusId,
				promptingToAdmit: fullVisit.promptingToAdmit,
				visitBuckets: fullVisit.visitBuckets.map(bucket => ({
					id: bucket.id,
					visitBucketStatusId: bucket.visitBucketStatusId,
					visitBucketStatusLabel: bucket.visitBucketStatusLabel,
					visitBucketTypeId: bucket.visitBucketTypeId,
					visitBucketTypeLabel: bucket.visitBucketTypeLabel
				}))
			};
		},
		initialDataUpdatedAt: () => queryClient.getQueryState(fullVisitOptionsQueryKey)?.dataUpdatedAt
	};
}

export function useGetVisitOptions(visitId: string) {
	const queryClient = useQueryClient();
	const fullVisitOptionsQueryKey = getFullVisitOptions(visitId).queryKey;

	return {
		...getVisitOptions(visitId, result => {
			const fullVisit = queryClient.getQueryData<FullVisitViewModel>(fullVisitOptionsQueryKey);
			if (fullVisit) {
				queryClient.setQueryData(
					fullVisitOptionsQueryKey,
					produce(fullVisit, draft => {
						draft = { ...fullVisit, ...result };
					})
				);
			}
		}),
		initialData: () => {
			const fullVisit = queryClient.getQueryData(fullVisitOptionsQueryKey);
			if (!fullVisit) {
				return undefined;
			}
			return fullVisit;
		},
		initialDataUpdatedAt: () => queryClient.getQueryState(fullVisitOptionsQueryKey)?.dataUpdatedAt
	};
}
